import { Autocomplete, AutocompleteInputChangeReason, Box, TextField } from '@mui/material';
import React, { useCallback } from 'react';

import { ISelectorValue } from '../../../types/shell';

interface ISelectorProps<T> {
  value: T | undefined | null;
  options: T[] | undefined;
  label?: string;
  onChange?: (value: T | null) => void;
  onInputChange?: (event: React.SyntheticEvent, text: string, reason: AutocompleteInputChangeReason) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  open?: boolean;
  onOpen?: (event: React.SyntheticEvent) => void;
  onClose?: (event: React.SyntheticEvent) => void;
  name?: string;
  required?: boolean;
  error?: string | string[] | null;
  dataAction?: string;
  autoComplete?: string;
}

export const Selector = <T extends ISelectorValue>({
  options,
  label,
  value,
  onChange,
  onInputChange,
  onBlur,
  open,
  onOpen,
  onClose,
  name,
  required = false,
  error,
  dataAction,
  autoComplete,
}: ISelectorProps<T>) => {
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: T | null) => {
      onChange?.(newValue);
    },
    [onChange],
  );

  return (
    <Box data-action={dataAction}>
      <Autocomplete
        options={options || []}
        value={value || null}
        onChange={handleChange}
        onInputChange={onInputChange}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        openOnFocus={false}
        clearOnBlur={false}
        blurOnSelect
        size="medium"
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            label={label}
            required={required}
            error={!!error}
            helperText={error}
            onBlur={onBlur}
            slotProps={{
              // Autocomplete hard-codes autocomplete="off" on its inner input, so the caller's
              // value has to be written onto the native input to reach the browser at all.
              htmlInput: { ...params.inputProps, ...(autoComplete ? { autoComplete } : {}) },
              input: params.InputProps,
            }}
            sx={{
              '& .MuiInputLabel-asterisk': { color: 'error.main' },

              '& .MuiFormHelperText-root': {
                position: 'absolute',
                bottom: -20,
                left: 0,
                margin: 0,
              },
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 200,
            },
          },
        }}
      />
    </Box>
  );
};
